// ─── Status Page (public uptime/incident page — replaces StatusCast) ─
// ONE vocabulary shared by the API (persistence, snapshot writer, notifier),
// the PCP admin area and the public page bundle. Everything is prefixed
// Status*/STATUS_* so it never collides with the KnowledgeAudience concept in
// types.ts. Deliberately dependency-free (no import from ./types.js) so this
// module can be pulled into the public page without dragging the whole DTO
// surface — and so there is no import cycle.
// ─── Component status ────────────────────────────────────────────────
// The four states a component can be in. These are the four StatusCast CSS
// classes, kept verbatim so the migrated history/markup stays meaningful.
export const STATUS_COMPONENT_STATUSES = ['available', 'degraded', 'maintenance', 'unavailable'];
/** Severity order for "worst wins" rollups (component → group → page).
 *  Maintenance ranks BELOW degraded on purpose: a real fault outranks planned
 *  work, so a component that is inside a maintenance window AND degraded must
 *  read as degraded — never hide a live problem behind a scheduled window. */
export const STATUS_COMPONENT_STATUS_RANK = {
    available: 0,
    maintenance: 1,
    degraded: 2,
    unavailable: 3,
};
/** The statuses that cost a component UPTIME. Maintenance is impactful (it
 *  paints the grid and drives the page banner) but is planned work, so it is
 *  not downtime: a component inside a maintenance window is billed only for a
 *  degraded/unavailable row that overlaps it, never for the window itself. */
export const STATUS_DOWNTIME_STATUSES = ['degraded', 'unavailable'];
/** Danish — the public page is Danish. */
export const STATUS_COMPONENT_STATUS_LABELS_DA = {
    available: 'Normal',
    degraded: 'Nedsat ydeevne',
    maintenance: 'Vedligeholdelse',
    unavailable: 'Driftsforstyrrelse',
};
/** English — the PCP admin area. */
export const STATUS_COMPONENT_STATUS_LABELS_EN = {
    available: 'Operational',
    degraded: 'Degraded performance',
    maintenance: 'Under maintenance',
    unavailable: 'Outage',
};
// ─── Impact ──────────────────────────────────────────────────────────
// What ONE incident does to ONE component. The three non-'none' members are
// intentionally named after the component status they induce (see
// impactToComponentStatus).
export const STATUS_IMPACTS = ['none', 'degraded', 'maintenance', 'unavailable'];
export const STATUS_IMPACT_LABELS_DA = {
    none: 'Ingen påvirkning',
    degraded: 'Nedsat ydeevne',
    maintenance: 'Vedligeholdelse',
    unavailable: 'Driftsforstyrrelse',
};
export const STATUS_IMPACT_LABELS_EN = {
    none: 'No impact',
    degraded: 'Degraded performance',
    maintenance: 'Under maintenance',
    unavailable: 'Outage',
};
// ─── Incident kind, lifecycle status, open/closed state ───────────────
export const STATUS_INCIDENT_KINDS = ['incident', 'maintenance', 'information'];
/** The union of every kind's lifecycle. Which ones are LEGAL for a given kind is
 *  STATUS_INCIDENT_KIND_STATUSES — 'resolved' is shared by incident + information. */
export const STATUS_INCIDENT_STATUSES = [
    'investigating', 'identified', 'monitoring', 'resolved',
    'scheduled', 'in_progress', 'completed', 'published',
];
export const STATUS_INCIDENT_STATES = ['open', 'closed'];
/** Only the two terminal statuses close an incident; 'published' information stays
 *  open until it is explicitly resolved. */
export const STATUS_INCIDENT_STATUS_STATE = {
    investigating: 'open',
    identified: 'open',
    monitoring: 'open',
    resolved: 'closed',
    scheduled: 'open',
    in_progress: 'open',
    completed: 'closed',
    published: 'open',
};
/** Legal statuses per kind, in lifecycle order — drives the status picker. */
export const STATUS_INCIDENT_KIND_STATUSES = {
    incident: ['investigating', 'identified', 'monitoring', 'resolved'],
    maintenance: ['scheduled', 'in_progress', 'completed'],
    information: ['published', 'resolved'],
};
export const STATUS_INCIDENT_DEFAULT_STATUS = {
    incident: 'investigating',
    maintenance: 'scheduled',
    information: 'published',
};
export const STATUS_INCIDENT_STATUS_LABELS_DA = {
    investigating: 'Undersøges',
    identified: 'Identificeret',
    monitoring: 'Overvåger',
    resolved: 'Løst',
    scheduled: 'Planlagt',
    in_progress: 'I gang',
    completed: 'Gennemført',
    published: 'Offentliggjort',
};
export const STATUS_INCIDENT_STATUS_LABELS_EN = {
    investigating: 'Investigating',
    identified: 'Identified',
    monitoring: 'Monitoring',
    resolved: 'Resolved',
    scheduled: 'Scheduled',
    in_progress: 'In progress',
    completed: 'Completed',
    published: 'Published',
};
// ─── Update kind ─────────────────────────────────────────────────────
// What an entry on the timeline IS, as opposed to what state it announces.
// A timeline heading is normally the lifecycle status ("Løst"), which leaves no
// way to tell the entry that CLOSED an incident from the root-cause write-up
// posted after it — both are 'resolved'. StatusCast posts the latter under its
// own "Hovedårsag" heading; Statuspage.io calls the same thing a postmortem.
// Deliberately a separate axis from `status`: a root cause is still 'resolved',
// so it keeps the green dot and the closed state.
export const STATUS_UPDATE_KINDS = ['update', 'root_cause'];
export const STATUS_UPDATE_KIND_LABELS_DA = {
    update: 'Opdatering',
    root_cause: 'Hovedårsag',
};
export const STATUS_UPDATE_KIND_LABELS_EN = {
    update: 'Update',
    root_cause: 'Root cause',
};
// ─── Incident templates ──────────────────────────────────────────────
/**
 * WHEN a template is offered — StatusCast's "Event" column.
 *
 * A property of the TEMPLATE, never of an incident: it decides which surface
 * lists it. PCP stores no event on an incident, and one could not be derived
 * anyway — updates carry no ordinal and occurred_at is deliberately backdatable,
 * so "is this the first update" has no answer in the schema.
 */
export const STATUS_TEMPLATE_STAGES = ['new', 'update', 'resolve'];
export const STATUS_TEMPLATE_STAGE_LABELS_DA = {
    new: 'Ny hændelse',
    update: 'Opdatering',
    resolve: 'Løsning',
};
export const STATUS_TEMPLATE_STAGE_LABELS_EN = {
    new: 'New incident',
    update: 'Update',
    resolve: 'Resolution',
};
export const isStatusTemplateStage = (s) => typeof s === 'string' && STATUS_TEMPLATE_STAGES.includes(s);
export const coerceStatusTemplateStage = (s) => isStatusTemplateStage(s) ? s : 'new';
export const STATUS_INCIDENT_STATE_LABELS_DA = { open: 'Åben', closed: 'Lukket' };
export const STATUS_INCIDENT_STATE_LABELS_EN = { open: 'Open', closed: 'Closed' };
export const STATUS_INCIDENT_KIND_LABELS_DA = {
    incident: 'Hændelse',
    maintenance: 'Systemopdatering',
    information: 'Information',
};
export const STATUS_INCIDENT_KIND_LABELS_EN = {
    incident: 'Incident',
    maintenance: 'Maintenance',
    information: 'Information',
};
// ─── Public filter chips ─────────────────────────────────────────────
// The seven chips on the public page's incident history. This is a DERIVED
// PROJECTION of (kind × lifecycle status × worst impact), never a stored enum.
export const STATUS_FILTER_LABELS = [
    'information', 'investigating', 'monitoring', 'identified', 'performance', 'outage', 'maintenance',
];
export const STATUS_FILTER_LABEL_LABELS_DA = {
    information: 'Information',
    investigating: 'Undersøges',
    monitoring: 'Overvåger',
    identified: 'Identificeret',
    performance: 'Performance',
    outage: 'Driftsforstyrrelse',
    maintenance: 'Systemopdatering',
};
export const STATUS_FILTER_LABEL_LABELS_EN = {
    information: 'Information',
    investigating: 'Investigating',
    monitoring: 'Monitoring',
    identified: 'Identified',
    performance: 'Performance',
    outage: 'Outage',
    maintenance: 'Maintenance',
};
/**
 * Chips for one incident. THREE independent axes (kind, lifecycle status, worst
 * component impact) collapse into a chip SET — not a single enum — because an
 * incident legitimately carries two at once: the live StatusCast page shows
 * "Undersøges" AND "Driftsforstyrrelse" on the same entry while it is being
 * worked. One enum cannot express that, so the chips are computed on read and
 * never persisted.
 * Output is in canonical STATUS_FILTER_LABELS order so it is stable across calls.
 */
export function incidentFilterLabels(input) {
    const chips = new Set();
    if (input.kind === 'information')
        chips.add('information');
    if (input.kind === 'maintenance')
        chips.add('maintenance');
    if (input.kind === 'incident') {
        // Lifecycle chip only while the incident is live — a resolved one keeps just
        // its impact chip (that's how the history list reads after the fact).
        if (input.status === 'investigating' || input.status === 'identified' || input.status === 'monitoring') {
            chips.add(input.status);
        }
        const worst = worstComponentStatus(input.impacts.map(impactToComponentStatus));
        if (worst === 'degraded')
            chips.add('performance');
        else if (worst === 'unavailable')
            chips.add('outage');
        else if (worst === 'maintenance')
            chips.add('maintenance');
    }
    return STATUS_FILTER_LABELS.filter((l) => chips.has(l));
}
// ─── Guards + coercion ───────────────────────────────────────────────
// A persisted value that is unknown (legacy import, hand-edited row) must
// DEGRADE to something sane, never throw — the public page has to render.
export const isStatusComponentStatus = (s) => STATUS_COMPONENT_STATUSES.includes(s);
export const coerceStatusComponentStatus = (s) => isStatusComponentStatus(s) ? s : 'available';
export const isStatusImpact = (s) => STATUS_IMPACTS.includes(s);
export const coerceStatusImpact = (s) => isStatusImpact(s) ? s : 'none';
export const isStatusIncidentKind = (s) => STATUS_INCIDENT_KINDS.includes(s);
export const coerceStatusIncidentKind = (s) => isStatusIncidentKind(s) ? s : 'incident';
export const isStatusUpdateKind = (s) => STATUS_UPDATE_KINDS.includes(s);
export const coerceStatusUpdateKind = (s) => isStatusUpdateKind(s) ? s : 'update';
export const isStatusIncidentStatus = (s) => STATUS_INCIDENT_STATUSES.includes(s);
/** Unknown OR illegal-for-this-kind (e.g. 'scheduled' on an incident, which the
 *  kind picker can produce mid-edit) falls back to the kind's first status. */
export const coerceStatusIncidentStatus = (s, kind) => isStatusIncidentStatus(s) && STATUS_INCIDENT_KIND_STATUSES[kind].includes(s)
    ? s
    : STATUS_INCIDENT_DEFAULT_STATUS[kind];
export const statusIncidentState = (s) => STATUS_INCIDENT_STATUS_STATE[s] ?? 'open';
// ─── Pure derivations ────────────────────────────────────────────────
/** The component status an impact induces. The three non-'none' impacts are named
 *  after their component status precisely so this is the identity — 'none' means
 *  "listed but untouched" and contributes nothing to the rollup. */
export function impactToComponentStatus(i) {
    return i === 'none' ? null : i;
}
/** Worst-wins reduction — THE single rollup used everywhere (open incidents →
 *  component, components → group, groups → page overall). Empty / all-null is
 *  'available': no signal means nothing is wrong. */
export function worstComponentStatus(values) {
    let worst = 'available';
    for (const v of values) {
        if (!v)
            continue;
        if (STATUS_COMPONENT_STATUS_RANK[v] > STATUS_COMPONENT_STATUS_RANK[worst])
            worst = v;
    }
    return worst;
}
// ─── Component tree ──────────────────────────────────────────────────
// Components NEST (root › child › grandchild). The depth limit used to be
// structural — components lived in a separate table from their groups, so a
// group could not contain a group. It is now a rule the service enforces on
// write, because the page we mirror puts a real, incident-bearing service at
// every level: `Mæglerunivers › Annoncering › Boligsiden` is three components,
// not a heading and two rows, and any of the three can be what an incident
// affected.
/** Root = 1. Three is what the source page uses; deeper reads as a sitemap. */
export const STATUS_COMPONENT_MAX_DEPTH = 3;
/**
 * Fold several 90-day history strings into one, worst status wins per day.
 *
 * This is what lets a COLLAPSED parent row still mean something: its grid is the
 * reduction of everything beneath it, so a visitor who never expands the
 * disclosure still sees the day a child was down.
 *
 * Days are compared position by position, so every input must already be the
 * same length and the same start date (they are — one componentHistory() call
 * produces them all). A shorter string simply contributes no data past its end
 * rather than throwing, since a status page must render.
 *
 * STATUS_HISTORY_NO_DATA is absorbing only when it is unanimous: one child with
 * real data on a day makes that day real for the parent. A day where every input
 * is '-' stays '-', which is what keeps the pre-existence gap honest.
 */
/**
 * OR a subtree's information masks together — the parallel to
 * foldHistoryStrings, and deliberately the only way a marker reaches a parent.
 *
 * A notice about one leaf marks its ancestors too, because collapsing a tree
 * must never hide a signal: a reader who has the root collapsed would otherwise
 * see nothing at all. It stops at the page-wide bar, which is folded WITHOUT any
 * mask so an announcement can never make the overall history look eventful.
 */
export function foldInfoMasks(masks, days) {
    const len = Math.max(0, Math.floor(days) || 0);
    if (!len)
        return '';
    const usable = masks.filter((m) => typeof m === 'string' && m.length > 0);
    if (!usable.length)
        return '';
    let out = '';
    for (let i = 0; i < len; i += 1)
        out += usable.some((m) => m[i] === '1') ? '1' : '0';
    return out;
}
export function foldHistoryStrings(histories, days) {
    const len = Math.max(0, Math.floor(days) || 0);
    if (!len)
        return '';
    const usable = histories.filter((h) => typeof h === 'string' && h.length > 0);
    if (!usable.length)
        return STATUS_HISTORY_NO_DATA.repeat(len);
    let out = '';
    for (let i = 0; i < len; i += 1) {
        let worst = null;
        for (const h of usable) {
            const status = STATUS_HISTORY_CHAR_STATUS[h[i]];
            if (!status)
                continue; // '-' or unrecognised: contributes nothing
            if (!worst || STATUS_COMPONENT_STATUS_RANK[status] > STATUS_COMPONENT_STATUS_RANK[worst]) {
                worst = status;
            }
        }
        out += worst ? STATUS_HISTORY_CHARS[worst] : STATUS_HISTORY_NO_DATA;
    }
    return out;
}
/**
 * Merge overlapping/abutting intervals into a disjoint, ascending set.
 *
 * The UNION, never the sum — two incidents hitting the same service at the same
 * time is one outage, not two, and adding their durations would let a component
 * report more downtime in a day than the day contains.
 */
export function mergeStatusIntervals(intervals) {
    const usable = intervals
        .filter((i) => Number.isFinite(i.start) && Number.isFinite(i.end) && i.end > i.start)
        .sort((a, b) => a.start - b.start);
    const merged = [];
    for (const interval of usable) {
        const last = merged[merged.length - 1];
        // `>=` so two windows that merely touch (one ends exactly as the next
        // begins) become one, rather than one continuous outage reported as two.
        if (last && interval.start <= last.end) {
            if (interval.end > last.end)
                last.end = interval.end;
        }
        else {
            merged.push({ start: interval.start, end: interval.end });
        }
    }
    return merged;
}
/**
 * Minutes of downtime inside each day, from the UNION of `intervals`.
 *
 * `dayBounds` is the local calendar-day axis (see componentHistory's "calendar
 * days, not UTC days" note) — DST days really are 23 or 25 hours wide, and
 * TODAY's bound ends at NOW, not at midnight, so a component that has been down
 * for ten minutes this morning is not measured against a day that has not
 * happened yet.
 *
 * Rounded to whole minutes per day, which is the unit the page reports in.
 */
export function downtimeMinutesPerDay(intervals, dayBounds) {
    const out = new Array(dayBounds.length).fill(0);
    const merged = mergeStatusIntervals(intervals);
    if (!merged.length)
        return out;
    // Both sides are ascending, so one forward pass over the intervals per day is
    // enough — `cursor` never rewinds.
    let cursor = 0;
    for (let d = 0; d < dayBounds.length; d += 1) {
        const day = dayBounds[d];
        if (!day || !(day.end > day.start))
            continue;
        let ms = 0;
        let i = cursor;
        let advanced = false;
        while (i < merged.length && merged[i].start < day.end) {
            const overlap = Math.min(merged[i].end, day.end) - Math.max(merged[i].start, day.start);
            if (overlap > 0)
                ms += overlap;
            // An interval ending inside this day can never touch a later one, so the
            // next day may start from the one after it.
            if (merged[i].end <= day.end && !advanced)
                cursor = i + 1;
            else
                advanced = true;
            i += 1;
        }
        out[d] = Math.round(ms / 60_000);
    }
    return out;
}
/**
 * Uptime as a 0..1 ratio, from MINUTES — or null when nothing was measured, so
 * the caller can print "Ingen data" instead of a fabricated 0 %.
 *
 * A day counts toward the denominator only when the grid has a real reading for
 * it (its char is not STATUS_HISTORY_NO_DATA): days before a component existed
 * are not days it was up. All three arrays are indexed in step — slice them
 * together and the figure describes exactly the window on screen.
 */
export function uptimeFromMinutes(history, downtime, dayMinutes) {
    let measured = 0;
    let down = 0;
    const len = Math.min(history.length, dayMinutes.length);
    for (let i = 0; i < len; i += 1) {
        if (!STATUS_HISTORY_CHAR_STATUS[history[i]])
            continue; // '-' or unrecognised
        const minutes = Number(dayMinutes[i]);
        if (!Number.isFinite(minutes) || minutes <= 0)
            continue;
        measured += minutes;
        const lost = Number(downtime[i]);
        if (Number.isFinite(lost) && lost > 0)
            down += Math.min(lost, minutes);
    }
    if (measured <= 0)
        return null;
    // Clamped: a rounding artefact must never print 100,1 % or a negative uptime.
    return Math.min(1, Math.max(0, (measured - down) / measured));
}
/**
 * Sum the per-day downtime of the days the grid actually measured — the total
 * a detail page prints beside the percentage ("nede i alt 4 t 15 min").
 */
export function totalDowntimeMinutes(history, downtime) {
    let total = 0;
    for (let i = 0; i < history.length; i += 1) {
        if (!STATUS_HISTORY_CHAR_STATUS[history[i]])
            continue;
        const lost = Number(downtime[i]);
        if (Number.isFinite(lost) && lost > 0)
            total += lost;
    }
    return total;
}
/** Human-facing reference id for an incident, e.g. i-00042. */
export function formatIncidentRefId(n) {
    return `i-${String(n).padStart(5, '0')}`;
}
const STATUS_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const STATUS_INCIDENT_REF_RE = /^i-0*(\d{1,9})$/i;
const STATUS_SOURCE_REF_RE = /^[A-Za-z0-9_-]{1,64}$/;
/**
 * Resolve whatever ended up in an /incident/:ref URL segment: our own uuid, our
 * ref id (i-00042), or a preserved StatusCast id (e.g. 693291).
 * The sourceRef arm exists so the old page's deep links — /incident/693291,
 * which are out in emails, tickets and forum posts — keep resolving after
 * cutover; the import carries the StatusCast id onto the row for exactly this.
 * Anything else → null (the caller 404s rather than guessing).
 */
export function parseIncidentRef(raw) {
    const s = String(raw || '').trim();
    if (!s)
        return null;
    if (STATUS_UUID_RE.test(s))
        return { kind: 'id', value: s };
    const ref = STATUS_INCIDENT_REF_RE.exec(s);
    if (ref)
        return { kind: 'number', value: parseInt(ref[1], 10) };
    if (STATUS_SOURCE_REF_RE.test(s))
        return { kind: 'sourceRef', value: s };
    return null;
}
/** An audience slug is a URL segment on the public page (/<slug>), so it must
 *  never shadow a page route or a served asset — those names are reserved. */
export const STATUS_RESERVED_SLUGS = [
    'api', 'assets', 'snapshot', 'status-data', 'incident', 'incidents', 'feed', 'history',
    // Both spellings, like incident/incidents above: the route only uses the
    // singular, but an audience slugged 'components' one letter away from a page
    // route is a trap nobody would find twice.
    'component', 'components',
    // The per-day page. Both spellings for the same reason as the pairs above —
    // an audience slugged 'days' beside a 'day' route is a trap nobody finds
    // twice. Reserving them matters more than usual here: the /day arm is tested
    // BEFORE the audience check, so an audience that managed to claim the slug
    // would be shadowed permanently AND would stop having snapshot files written.
    'day', 'days',
    'subscribe', 'unsubscribe', 'confirm', 'manage',
    'sw.js', 'manifest.json', 'robots.txt', 'status.html',
];
export const STATUS_SLUG_RE = /^[a-z0-9][a-z0-9-]{1,63}$/;
export const isValidStatusSlug = (s) => STATUS_SLUG_RE.test(s) && !STATUS_RESERVED_SLUGS.includes(s);
/** Suggest a slug from an audience name. Danish letters transliterate to digraphs
 *  (æ→ae, ø→oe, å→aa) BEFORE the generic diacritic strip — NFD leaves æ/ø intact
 *  and would fold å to a bare "a", which reads wrong to a Danish user. */
export function slugifyStatusName(name) {
    return String(name || '')
        .normalize('NFC') // fold a decomposed "å" back together so the map below sees it
        .toLowerCase()
        .replace(/æ/g, 'ae')
        .replace(/ø/g, 'oe')
        .replace(/å/g, 'aa')
        .normalize('NFD')
        .replace(/\p{Mn}/gu, '') // drop the combining marks NFD just split off (ä → a, é → e)
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64)
        .replace(/-+$/, ''); // the truncation can land on a separator
}
/** The 90-day grid ships as ONE CHARACTER PER DAY per component: 28 components ×
 *  90 days ≈ 2.5 KB, versus ~200 KB as an array of objects. That is the whole
 *  reason the public payload can be a single cacheable static file. */
export const STATUS_HISTORY_CHARS = {
    available: 'o',
    degraded: 'd',
    maintenance: 'n',
    unavailable: 'm',
};
/** Day with no recorded data (before the component existed / gaps in the import). */
export const STATUS_HISTORY_NO_DATA = '-';
/** Reverse of STATUS_HISTORY_CHARS. Undefined for '-' and anything unrecognised —
 *  callers must render those as "no data". */
export const STATUS_HISTORY_CHAR_STATUS = {
    o: 'available',
    d: 'degraded',
    n: 'maintenance',
    m: 'unavailable',
};
/** Which layer forced a send to be emulated. Precedence is exactly this order —
 *  'env' cannot be overridden by anything, and 'request' can only ever turn
 *  simulation ON (a header must never be able to switch real sending on). */
export const STATUS_SIMULATION_REASONS = ['env', 'setting', 'request'];
export const STATUS_SIMULATION_REASON_LABELS = {
    env: 'Simulation forced by STATUS_NOTIFY_DRY_RUN on this environment',
    setting: 'Live sending is not armed (Settings → Status Page → Simulation mode)',
    request: 'This request asked for a dry run',
};
/** The header that forces a single request to emulate. One-way: present and
 *  truthy ⇒ simulate. Its absence never enables real sending on its own. */
export const STATUS_DRY_RUN_HEADER = 'x-pcp-dry-run';
export const DEFAULT_STATUS_PAGE_SETTINGS = {
    // A fresh deploy sends nothing to nobody: simulation ON, both channels off,
    // public subscribe off, and no "allow any" — so even a mis-seeded subscriber
    // list can't be mailed until an admin deliberately opens each gate.
    simulationMode: true,
    emailEnabled: false,
    smsEnabled: false,
    emailAllowAny: false,
    smsAllowAny: false,
    emailAllowlist: [],
    smsAllowlist: [],
    fromAddress: '',
    fromName: 'C&B Driftsstatus',
    replyTo: '',
    postmarkTransactionalStream: 'outbound',
    postmarkBroadcastStream: 'broadcast',
    postmarkWebhookIps: ['3.134.147.250', '50.31.156.6', '50.31.156.77', '18.217.206.57'],
    // 3-11 ALPHANUMERIC characters — validateGatewayApiSender rejects anything
    // else, and send-gate.ts disables the whole SMS channel when it fails. So not
    // 'C&B' (the ampersand is punctuation) and not 'CB' (too short).
    smsSender: 'CBdrift',
    smsDefaultCountryCode: '45',
    smsPriority: 'normal',
    smsExpiration: 'P5D',
    gatewayApiBaseUrl: 'https://messaging.gatewayapi.eu',
    // Empty = defer to the stack's STATUS_PUBLIC_ORIGIN. A default hostname here
    // would be wrong on every stack but one.
    publicOrigin: '',
    subscribeEnabled: false,
    turnstileSiteKey: '',
    dailyConfirmEmailCap: 200,
    dailyConfirmSmsCap: 50,
    dailyNotifyEmailCap: 5000,
    dailyNotifySmsCap: 500,
    minFormDwellMs: 2500,
    notifyEmailDefaultSeverities: ['degraded', 'unavailable'],
};
/**
 * The public hostnames a stack can be told to send customers to.
 *
 * A closed list, not free text: this string ends up in every confirmation and
 * unsubscribe link that leaves the building, and a typo in it is discovered by a
 * customer clicking a dead link days later. The public status page answers on
 * exactly these names (see the @status host matcher in deploy/Caddyfile) — keep
 * the two in step.
 *
 * `status.cb.dk` is the customer-facing name inherited from StatusCast and is
 * the one to use once that cutover is complete.
 */
/**
 * The two hosts GatewayAPI serves the messaging API on.
 *
 * A CLOSED LIST, not free text — the same argument as STATUS_PUBLIC_ORIGIN_CHOICES
 * below. Both hosts expose the identical `POST /mobile/multi` and the identical
 * `Authorization: Token …` header, so a token issued on one platform is simply
 * NOT AUTHORISED on the other and comes back **403 Forbidden** — which reads as
 * an authentication failure and sends an operator off to re-paste a credential
 * that was correct all along (b-01652). A value with two legal spellings, whose
 * wrong choice surfaces far from where it was typed, is picked and not typed.
 *
 * The EU platform is the default: it is where C&B sends from.
 */
export const GATEWAYAPI_BASE_URL_CHOICES = [
    { value: 'https://messaging.gatewayapi.eu', label: 'messaging.gatewayapi.eu — the EU platform' },
    { value: 'https://messaging.gatewayapi.com', label: 'messaging.gatewayapi.com — the global platform' },
];
export const STATUS_PUBLIC_ORIGIN_CHOICES = [
    { value: '', label: 'Use the stack default (STATUS_PUBLIC_ORIGIN)' },
    { value: 'https://status.cb.dk', label: 'status.cb.dk — the customer-facing name' },
    { value: 'https://status.realequity.ai', label: 'status.realequity.ai' },
    { value: 'https://status.int.realequity.ai', label: 'status.int.realequity.ai (INT only)' },
];
/** Credentials that live sealed in the secrets store, NOT in settings — they are
 *  write-only from the admin UI and only ever surfaced as a SecretStatus. */
export const SEALED_STATUS_SECRET_NAMES = [
    'statusPage.postmarkServerToken',
    'statusPage.postmarkWebhookBasicAuth',
    'statusPage.gatewayApiToken',
    'statusPage.gatewayApiWebhookSecret',
    'statusPage.turnstileSecretKey',
];
//# sourceMappingURL=status.js.map